import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export function setAuthToken(token) {
  if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete api.defaults.headers.common['Authorization'];
}

// Load token on init
if (typeof window !== 'undefined') {
  const saved = localStorage.getItem('kh_token');
  if (saved) setAuthToken(saved);
}

// 401 → auto redirect to login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && typeof window !== 'undefined') {
      localStorage.removeItem('kh_token');
      setAuthToken(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (username, password) =>
  api.post('/auth/login', { username, password }).then((r) => r.data);
export const fetchMe = () => api.get('/auth/me').then((r) => r.data);
export const changePassword = (old_password, new_password) =>
  api.post('/auth/change-password', { old_password, new_password }).then((r) => r.data);

// Branches
export const listBranches = () => api.get('/branches').then((r) => r.data);
export const createBranch = (name) => api.post('/branches', { name }).then((r) => r.data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`).then((r) => r.data);

// Entries
export const listEntries = (branch_id, year, month) =>
  api.get('/entries', { params: { branch_id, year, month } }).then((r) => r.data);
export const upsertEntry = (payload) => api.post('/entries', payload).then((r) => r.data);
export const deleteEntry = (id) => api.delete(`/entries/${id}`).then((r) => r.data);

// Summary
export const monthlySummary = (year, month) =>
  api.get('/summary', { params: { year, month } }).then((r) => r.data);

// Users
export const listUsers = () => api.get('/users').then((r) => r.data);
export const createUser = (payload) => api.post('/users', payload).then((r) => r.data);
export const updateUser = (id, payload) => api.patch(`/users/${id}`, payload).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);

// Audit
export const listAudit = (params) => api.get('/audit', { params }).then((r) => r.data);

// Notifications
export const listNotifications = (params) =>
  api.get('/notifications', { params }).then((r) => r.data);
export const markReadNotifications = (ids) =>
  api.post('/notifications/mark-read', { ids: ids || null }).then((r) => r.data);

// Accounts (Chart of Accounts)
export const listAccounts = (params) => api.get('/accounts', { params }).then((r) => r.data);
export const createAccount = (payload) => api.post('/accounts', payload).then((r) => r.data);
export const updateAccount = (id, payload) => api.patch(`/accounts/${id}`, payload).then((r) => r.data);
export const deleteAccount = (id) => api.delete(`/accounts/${id}`).then((r) => r.data);
export const importMasterCoa = (dry_run = true) =>
  api.post('/accounts/import-master-coa', null, { params: { dry_run } }).then((r) => r.data);

// Journals (Jurnal Umum)
export const listJournals = (params) => api.get('/journals', { params }).then((r) => r.data);
export const createJournal = (payload) => api.post('/journals', payload).then((r) => r.data);
export const updateJournal = (id, payload) => api.patch(`/journals/${id}`, payload).then((r) => r.data);
export const deleteJournal = (id) => api.delete(`/journals/${id}`).then((r) => r.data);

// Uploads
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};
export const attachmentUrl = (url) => url ? `${BACKEND_URL}${url}` : '';

// Ledger (Buku Besar)
export const accountLedger = (accountId, params) =>
  api.get(`/ledger/${accountId}`, { params }).then((r) => r.data);

// Reports
export const reportProfitLoss = (params) =>
  api.get('/reports/profit-loss', { params }).then((r) => r.data);
export const reportBalanceSheet = (params) =>
  api.get('/reports/balance-sheet', { params }).then((r) => r.data);
export const reportTrend = (params) =>
  api.get('/reports/trend', { params }).then((r) => r.data);
export const dashboardSummary = () => api.get('/dashboard/summary').then((r) => r.data);

// Vendors & Bills
export const listVendors = () => api.get('/vendors').then((r) => r.data);
export const createVendor = (payload) => api.post('/vendors', payload).then((r) => r.data);
export const updateVendor = (id, payload) => api.patch(`/vendors/${id}`, payload).then((r) => r.data);
export const deleteVendor = (id) => api.delete(`/vendors/${id}`).then((r) => r.data);
export const listVendorBills = (params) => api.get('/vendor-bills', { params }).then((r) => r.data);
export const createVendorBill = (payload) => api.post('/vendor-bills', payload).then((r) => r.data);
export const updateVendorBill = (id, payload) => api.patch(`/vendor-bills/${id}`, payload).then((r) => r.data);
export const deleteVendorBill = (id) => api.delete(`/vendor-bills/${id}`).then((r) => r.data);
export const listBillPayments = (billId) => api.get(`/vendor-bills/${billId}/payments`).then((r) => r.data);
export const createBillPayment = (billId, payload) => api.post(`/vendor-bills/${billId}/payments`, payload).then((r) => r.data);
export const deleteBillPayment = (id) => api.delete(`/vendor-payments/${id}`).then((r) => r.data);

// Employees & Payroll
export const listEmployees = () => api.get('/employees').then((r) => r.data);
export const createEmployee = (payload) => api.post('/employees', payload).then((r) => r.data);
export const updateEmployee = (id, payload) => api.patch(`/employees/${id}`, payload).then((r) => r.data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`).then((r) => r.data);
export const listPayroll = (params) => api.get('/payroll-runs', { params }).then((r) => r.data);
export const createPayroll = (payload) => api.post('/payroll-runs', payload).then((r) => r.data);
export const deletePayroll = (id) => api.delete(`/payroll-runs/${id}`).then((r) => r.data);

// Fixed Assets (Aset Tetap + Penyusutan)
export const listFixedAssets = () => api.get('/fixed-assets').then((r) => r.data);
export const createFixedAsset = (payload) => api.post('/fixed-assets', payload).then((r) => r.data);
export const updateFixedAsset = (id, payload) => api.patch(`/fixed-assets/${id}`, payload).then((r) => r.data);
export const deleteFixedAsset = (id) => api.delete(`/fixed-assets/${id}`).then((r) => r.data);
export const postDepreciation = (id, month) =>
  api.post(`/fixed-assets/${id}/post-depreciation`, null, { params: month ? { month } : {} }).then((r) => r.data);
export const postAllDepreciation = (month) =>
  api.post('/fixed-assets/post-all', null, { params: month ? { month } : {} }).then((r) => r.data);
export const reverseDepreciation = (id, month) =>
  api.post(`/fixed-assets/${id}/reverse-depreciation`, null, { params: { month } }).then((r) => r.data);

// Photo Import (Foto Laporan → Jurnal Otomatis)
export const uploadPhotoImport = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/photo-imports/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then((r) => r.data);
};
export const updatePhotoImport = (id, payload) =>
  api.patch(`/photo-imports/${id}`, payload).then((r) => r.data);
export const postPhotoImport = (id) =>
  api.post(`/photo-imports/${id}/post`).then((r) => r.data);
export const listPhotoImports = () =>
  api.get('/photo-imports').then((r) => r.data);
export const getPhotoImport = (id) =>
  api.get(`/photo-imports/${id}`).then((r) => r.data);
export const deletePhotoImport = (id) =>
  api.delete(`/photo-imports/${id}`).then((r) => r.data);

// Reminders (Kalender & Pengingat)
export const listReminders = () => api.get('/reminders').then((r) => r.data);
export const createReminder = (payload) => api.post('/reminders', payload).then((r) => r.data);
export const updateReminder = (id, payload) => api.patch(`/reminders/${id}`, payload).then((r) => r.data);
export const deleteReminder = (id) => api.delete(`/reminders/${id}`).then((r) => r.data);
export const toggleReminderPaid = (id, month) =>
  api.post(`/reminders/${id}/toggle-paid`, null, { params: { month } }).then((r) => r.data);
export const moveReminder = (id, direction) =>
  api.post(`/reminders/${id}/move`, null, { params: { direction } }).then((r) => r.data);

// Marketplace Products (Kalkulator + Database Produk)
export const listMarketplaceProducts = (q, product_type, brand) =>
  api.get('/marketplace-products', { params: {
    ...(q ? { q } : {}),
    ...(product_type && product_type !== 'all' ? { product_type } : {}),
    ...(brand && brand !== 'all' ? { brand } : {}),
  } }).then((r) => r.data);
export const listMarketplaceBrands = () =>
  api.get('/marketplace-products/brands').then((r) => r.data);
export const getMarketplaceSettings = () =>
  api.get('/marketplace-settings').then((r) => r.data);
export const saveMarketplaceSettings = (payload) =>
  api.put('/marketplace-settings', payload).then((r) => r.data);
export const recalcAllMarketplaceProducts = () =>
  api.post('/marketplace-products/recalculate-all').then((r) => r.data);
export const recalcMarketplaceProduct = (id) =>
  api.post(`/marketplace-products/${id}/recalculate`).then((r) => r.data);

// Sales Products
export const listSalesProducts = (params = {}) =>
  api.get('/sales-products', { params }).then((r) => r.data);
export const getSalesProduct = (id) =>
  api.get(`/sales-products/${id}`).then((r) => r.data);
export const createSalesProduct = (payload) =>
  api.post('/sales-products', payload).then((r) => r.data);
export const updateSalesProduct = (id, payload) =>
  api.patch(`/sales-products/${id}`, payload).then((r) => r.data);
export const deleteSalesProduct = (id) =>
  api.delete(`/sales-products/${id}`).then((r) => r.data);
export const bulkDeleteSalesProducts = (ids) =>
  api.post('/sales-products/bulk-delete', { ids }).then((r) => r.data);
export const importSalesProducts = (mode, items) =>
  api.post('/sales-products/import', { mode, items }).then((r) => r.data);
export const listSalesBrands = () =>
  api.get('/sales-products/brands').then((r) => r.data);
export const listSalesCategories = () =>
  api.get('/sales-products/categories').then((r) => r.data);
export const salesProductStats = () =>
  api.get('/sales-products/stats').then((r) => r.data);

// Catalog presets
export const listCatalogPresets = () =>
  api.get('/catalog-presets').then((r) => r.data);
export const createCatalogPreset = (payload) =>
  api.post('/catalog-presets', payload).then((r) => r.data);
export const deleteCatalogPreset = (id) =>
  api.delete(`/catalog-presets/${id}`).then((r) => r.data);
export const getMarketplaceProduct = (id) =>
  api.get(`/marketplace-products/${id}`).then((r) => r.data);
export const createMarketplaceProduct = (payload) =>
  api.post('/marketplace-products', payload).then((r) => r.data);
export const updateMarketplaceProduct = (id, payload) =>
  api.patch(`/marketplace-products/${id}`, payload).then((r) => r.data);
export const duplicateMarketplaceProduct = (id) =>
  api.post(`/marketplace-products/${id}/duplicate`).then((r) => r.data);
export const deleteMarketplaceProduct = (id) =>
  api.delete(`/marketplace-products/${id}`).then((r) => r.data);

// PO Import
export const listPOVendors = () =>
  api.get('/po-vendors').then((r) => r.data);
export const createPOVendor = (payload) =>
  api.post('/po-vendors', payload).then((r) => r.data);
export const updatePOVendor = (id, payload) =>
  api.patch(`/po-vendors/${id}`, payload).then((r) => r.data);
export const listPOMethods = () =>
  api.get('/po-methods').then((r) => r.data);
export const createPOMethod = (payload) =>
  api.post('/po-methods', payload).then((r) => r.data);
export const updatePOMethod = (id, payload) =>
  api.patch(`/po-methods/${id}`, payload).then((r) => r.data);
export const deletePOMethod = (id) =>
  api.delete(`/po-methods/${id}`).then((r) => r.data);
export const listPOImports = () =>
  api.get('/po-imports').then((r) => r.data);
export const createPOImport = (payload) =>
  api.post('/po-imports', payload).then((r) => r.data);
export const updatePOImport = (id, payload) =>
  api.patch(`/po-imports/${id}`, payload).then((r) => r.data);
export const startPOShipment = (id) =>
  api.post(`/po-imports/${id}/start-shipment`).then((r) => r.data);
export const completePOImport = (id) =>
  api.post(`/po-imports/${id}/complete`).then((r) => r.data);
export const deletePOImport = (id) =>
  api.delete(`/po-imports/${id}`).then((r) => r.data);

// Payment Notes
export const listPaymentNotes = (year, month) =>
  api.get('/payment-notes', { params: { year, month } }).then((r) => r.data);
export const createPaymentNote = (payload) =>
  api.post('/payment-notes', payload).then((r) => r.data);
export const updatePaymentNote = (id, payload) =>
  api.patch(`/payment-notes/${id}`, payload).then((r) => r.data);
export const deletePaymentNote = (id) =>
  api.delete(`/payment-notes/${id}`).then((r) => r.data);

// Exports
// -------------------- Stock Opname --------------------
export const opnameModuleInfo = () =>
  api.get('/opname/module-info').then((r) => r.data);
export const opnameMasterInfo = () =>
  api.get('/opname/master').then((r) => r.data);
export const opnameUploadMaster = (payload) =>
  api.post('/opname/master', payload).then((r) => r.data);
export const opnameRows = (params) =>
  api.get('/opname/rows', { params }).then((r) => r.data);
export const opnameListDiff = () =>
  api.get('/opname/opname-list').then((r) => r.data);
export const opnameStats = () =>
  api.get('/opname/stats').then((r) => r.data);
export const opnameFacets = () =>
  api.get('/opname/facets').then((r) => r.data);
export const opnameSaveCount = (payload) =>
  api.post('/opname/count', payload).then((r) => r.data);
export const opnameSettingsGet = () =>
  api.get('/opname/settings').then((r) => r.data);
export const opnameSettingsUpdate = (payload) =>
  api.patch('/opname/settings', payload).then((r) => r.data);

// Pajak Marketplace (PPh 22)
export const listPajak = (params) =>
  api.get('/pajak/transactions', { params }).then((r) => r.data);
export const pajakSummary = (params) =>
  api.get('/pajak/summary', { params }).then((r) => r.data);
export const pajakReport = () =>
  api.get('/pajak/report').then((r) => r.data);
export const createPajak = (payload) =>
  api.post('/pajak/transactions', payload).then((r) => r.data);
export const updatePajak = (id, payload) =>
  api.patch(`/pajak/transactions/${id}`, payload).then((r) => r.data);
export const deletePajak = (id) =>
  api.delete(`/pajak/transactions/${id}`).then((r) => r.data);
export const importPajak = (rows) =>
  api.post('/pajak/import', { rows }).then((r) => r.data);

export const downloadExport = async (path, params, filename) => {
  const res = await api.get(path, { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
